import { Search, X } from 'lucide-react';
import { useEffect, useState } from 'react';

import { DateRangePicker } from '@/components/DateRangePicker';
import EditSavedSearchDialog from '@/components/EditSavedSearchDialog';
import {
  MultiSelectOption,
  MultiSelectPopover,
} from '@/components/MultiSelectPopover';
import { SavedSearchSelect } from '@/components/SavedSearchSelect';
import { SelectedChips } from '@/components/SelectedChips';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useFilters } from '@/contexts/FilterContext';
import { useDebounce } from '@/hooks/useDebounce';
import { useSymbols } from '@/hooks/useSymbols';
import { track } from '@/lib/mixpanel';
import { ArticleSource, getDisplaySourceName } from '@/schema';

function formatSelectionSummary(
  count: number,
  defaultLabel: string,
  options: { singular: string; plural: string }
) {
  return count > 0
    ? `${count} ${count === 1 ? options.singular : options.plural}`
    : defaultLabel;
}

export function NewsFilters() {
  const [localSearchQuery, setLocalSearchQuery] = useState('');
  const [isSaveDialogOpen, setIsSaveDialogOpen] = useState(false);
  const [saveSearchName, setSaveSearchName] = useState('');
  const [saveError, setSaveError] = useState('');
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);

  const {
    selectedSymbols,
    setSelectedSymbols,
    selectedSources,
    setSelectedSources,
    dateRange,
    setDateRange,
    searchQuery,
    setSearchQuery,
    hasSymbolOnly,
    setHasSymbolOnly,
    clearAllFilters,
    savedSearches,
    savedSearchActions,
    activeSavedSearchId,
    applySavedSearch,
  } = useFilters();

  useEffect(() => {
    setLocalSearchQuery(searchQuery);
  }, [searchQuery]);

  const debouncedSearchQuery = useDebounce(localSearchQuery, 300);

  useEffect(() => {
    setSearchQuery(debouncedSearchQuery);
  }, [debouncedSearchQuery, setSearchQuery]);

  const { data: symbolsData, isLoading: symbolsLoading } = useSymbols();

  const handleSymbolSelect = (symbol: string) => {
    const isAdding = !selectedSymbols.includes(symbol);
    if (selectedSymbols.includes(symbol)) {
      setSelectedSymbols(selectedSymbols.filter((s) => s !== symbol));
    } else {
      setSelectedSymbols([...selectedSymbols, symbol]);
    }
    track('Homepage_SymbolToggled', {
      symbol,
      action: isAdding ? 'add' : 'remove',
    });
  };

  const removeSymbol = (symbolToRemove: string) => {
    setSelectedSymbols(selectedSymbols.filter((s) => s !== symbolToRemove));
    track('Homepage_SymbolRemoved', { symbol: symbolToRemove });
  };

  const handleSourceSelect = (source: ArticleSource) => {
    const isAdding = !selectedSources.includes(source);
    if (selectedSources.includes(source)) {
      setSelectedSources(selectedSources.filter((s) => s !== source));
    } else {
      setSelectedSources([...selectedSources, source]);
    }
    track('Homepage_SourceToggled', {
      source,
      action: isAdding ? 'add' : 'remove',
    });
  };

  const removeSource = (sourceToRemove: string) => {
    setSelectedSources(selectedSources.filter((s) => s !== sourceToRemove));
    track('Homepage_SourceRemoved', { source: sourceToRemove });
  };

  const handleClearFilters = () => {
    track('Homepage_ClearFiltersClicked', {
      symbolCount: selectedSymbols.length,
      sourceCount: selectedSources.length,
      hasDateRange: Boolean(dateRange?.from || dateRange?.to),
      hasSearchQuery: Boolean(searchQuery),
      hasSymbolOnly,
    });
    clearAllFilters();
    setLocalSearchQuery('');
  };

  const activeSavedSearch =
    activeSavedSearchId !== null
      ? savedSearches.find((search) => search.id === activeSavedSearchId)
      : undefined;

  const hasFiltersForSavedSearch =
    selectedSymbols.length > 0 || selectedSources.length > 0 || !hasSymbolOnly;

  const savedSearchNameExists = (name: string, excludeId?: number) =>
    savedSearches.some(
      (search) =>
        search.name.toLowerCase() === name.toLowerCase() &&
        search.id !== excludeId
    );

  const buildSuggestedName = () => {
    const baseName = 'Căutare salvată';
    const existingNames = new Set(
      savedSearches.map((search) => search.name.toLowerCase())
    );

    if (!existingNames.has(baseName.toLowerCase())) {
      return baseName;
    }

    let index = savedSearches.length + 1;
    let candidate = `${baseName} ${index}`;

    while (existingNames.has(candidate.toLowerCase())) {
      index += 1;
      candidate = `${baseName} ${index}`;
    }

    return candidate;
  };

  const handleSavedSearchSelection = (value: string) => {
    if (value === '__clear__') {
      track('Homepage_SavedSearchCleared');
      // Clear all filters and reset the active saved search
      clearAllFilters();
      setLocalSearchQuery('');
      return;
    }

    const parsedId = Number(value);
    if (Number.isNaN(parsedId)) {
      return;
    }

    track('Homepage_SavedSearchApplied', { savedSearchId: parsedId });
    applySavedSearch(parsedId);
  };

  const handleOpenSaveDialog = () => {
    track('Homepage_SaveSearchDialogOpened', {
      symbolCount: selectedSymbols.length,
      sourceCount: selectedSources.length,
    });
    setSaveSearchName(buildSuggestedName());
    setSaveError('');
    setIsSaveDialogOpen(true);
  };

  const handleSaveSearch = async () => {
    const trimmedName = saveSearchName.trim();

    if (!hasFiltersForSavedSearch) {
      setSaveError(
        'Selectează cel puțin una dintre surse, simboluri sau filtrul „Doar cu simbol detectat” înainte de a salva.'
      );
      return;
    }

    if (!trimmedName) {
      setSaveError('Introdu un nume pentru căutare.');
      return;
    }

    if (savedSearchNameExists(trimmedName)) {
      setSaveError('Există deja o căutare cu acest nume.');
      return;
    }

    setSaveError('');

    try {
      const newSearch = await savedSearchActions.create({
        name: trimmedName,
        filters: {
          symbols: selectedSymbols,
          sources: selectedSources,
          hasSymbolOnly,
        },
      });

      track('Homepage_SaveSearchSuccess', {
        savedSearchId: newSearch.id,
        symbolCount: selectedSymbols.length,
        sourceCount: selectedSources.length,
        hasSymbolOnly,
      });
      applySavedSearch(newSearch.id);
      setIsSaveDialogOpen(false);
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : 'A apărut o eroare la salvarea căutării.';
      track('Homepage_SaveSearchFailed', { error: message });
      setSaveError(message);
    }
  };

  const handleOpenEditDialog = () => {
    if (!activeSavedSearch) return;
    track('Homepage_EditSearchDialogOpened', {
      savedSearchId: activeSavedSearch.id,
    });
    setIsEditDialogOpen(true);
  };

  const predefinedSources = Object.values(ArticleSource) as ArticleSource[];

  const symbolValues = Array.isArray(symbolsData?.symbols)
    ? (symbolsData.symbols as string[])
    : [];

  const symbolOptions: MultiSelectOption[] = symbolValues.map((symbol) => ({
    value: symbol,
    label: symbol,
    isSelected: selectedSymbols.includes(symbol),
  }));

  const sourceOptions: MultiSelectOption[] = predefinedSources.map(
    (source) => ({
      value: source,
      label: getDisplaySourceName(source),
      isSelected: selectedSources.includes(source),
    })
  );

  const sourceChips = selectedSources.map((source) => {
    const label = getDisplaySourceName(source as ArticleSource);

    return {
      value: source,
      label,
      removeLabel: `Elimină sursa ${label}`,
    };
  });

  const symbolChips = selectedSymbols.map((symbol) => ({
    value: symbol,
    label: symbol,
    removeLabel: `Elimină simbolul ${symbol}`,
  }));

  const symbolSummary = formatSelectionSummary(
    selectedSymbols.length,
    'Selectează simboluri...',
    {
      singular: 'simbol selectat',
      plural: 'simboluri selectate',
    }
  );

  const sourceSummary = formatSelectionSummary(
    selectedSources.length,
    'Selectează surse...',
    {
      singular: 'sursă selectată',
      plural: 'surse selectate',
    }
  );

  const isAnyFilterActive =
    selectedSymbols.length > 0 ||
    selectedSources.length > 0 ||
    Boolean(dateRange?.from || dateRange?.to) ||
    Boolean(searchQuery) ||
    !hasSymbolOnly;

  return (
    <div className="mb-6 flex flex-col gap-4">
      <div className="w-full text-left">
        <h2 className="font-heading text-3xl font-bold text-primary md:text-4xl">
          Ultimele știri
        </h2>
        <p className="ml-1 mt-2 text-neutral-600">
          Știri financiare de la cele mai importante surse din România
        </p>
      </div>

      {/* Filters Card */}
      <div className="rounded-2xl border border-neutral-200 bg-white p-4 shadow-sm md:p-6">
        {/* Top row: Saved searches + Search input */}
        <div className="mb-5 flex flex-col gap-4 md:mb-6 md:flex-row md:items-end md:justify-between">
          <div className="flex flex-wrap items-end gap-3">
            <SavedSearchSelect
              savedSearches={savedSearches}
              value={
                activeSavedSearchId !== null ? String(activeSavedSearchId) : ''
              }
              onValueChange={handleSavedSearchSelection}
              hasActiveSavedSearch={Boolean(activeSavedSearch)}
            />
            {activeSavedSearch && (
              <Button variant="outline" onClick={handleOpenEditDialog}>
                Editează căutarea
              </Button>
            )}
            {!activeSavedSearch && hasFiltersForSavedSearch && (
              <Button variant="outline" onClick={handleOpenSaveDialog}>
                Salvează căutarea
              </Button>
            )}
          </div>

          <div className="relative w-full md:w-72">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 transform text-neutral-400" />
            <Input
              type="text"
              placeholder="Caută știri..."
              value={localSearchQuery}
              onChange={(e) => setLocalSearchQuery(e.target.value)}
              className="h-9 w-full rounded-lg border-neutral-200 bg-white py-2 pl-10 pr-4 focus:border-neutral-400 focus:ring-1 focus:ring-neutral-400"
            />
          </div>
        </div>

        {/* Filter buttons row */}
        <div className="flex flex-wrap items-center gap-3">
          <MultiSelectPopover
            summary={sourceSummary}
            options={sourceOptions}
            onSelect={(value) => handleSourceSelect(value as ArticleSource)}
            searchPlaceholder="Caută sursă..."
            emptyMessage="Nicio sursă găsită."
          />

          <MultiSelectPopover
            summary={symbolSummary}
            options={symbolOptions}
            onSelect={handleSymbolSelect}
            searchPlaceholder="Caută simbol..."
            emptyMessage="Niciun simbol găsit."
            loadingMessage="Se încarcă simboluri..."
            isLoading={symbolsLoading}
          />

          <DateRangePicker
            dateRange={dateRange}
            onChange={setDateRange}
            onClear={() => setDateRange(undefined)}
          />

          {isAnyFilterActive && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleClearFilters}
              className="text-neutral-500 hover:bg-neutral-100 hover:text-neutral-700">
              Șterge filtrele
              <X className="ml-1 h-4 w-4" />
            </Button>
          )}
        </div>

        {/* Checkbox row */}
        <div className="ml-2 mt-5 flex items-center space-x-2">
          <Checkbox
            id="hasSymbol"
            checked={hasSymbolOnly}
            onCheckedChange={(checked) => {
              setHasSymbolOnly(Boolean(checked));
              track('Homepage_HasSymbolOnlyToggled', {
                enabled: Boolean(checked),
              });
            }}
          />
          <label
            htmlFor="hasSymbol"
            className="cursor-pointer pl-2 text-sm font-medium leading-none text-neutral-700">
            Doar cu simbol detectat
          </label>
        </div>

        {/* Selected chips */}
        {(sourceChips.length > 0 || symbolChips.length > 0) && (
          <div className="mt-4 flex flex-wrap gap-2">
            <SelectedChips items={sourceChips} onRemove={removeSource} />
            <SelectedChips items={symbolChips} onRemove={removeSymbol} />
          </div>
        )}
      </div>

      <Dialog
        open={isSaveDialogOpen}
        onOpenChange={(open) => {
          setIsSaveDialogOpen(open);
          if (!open) {
            setSaveError('');
          }
        }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Salvează căutarea curentă</DialogTitle>
            <DialogDescription>
              Se vor salva sursele, simbolurile și opțiunea „Doar cu simbol
              detectat” setate în prezent.
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-3">
            <Label htmlFor="saved-search-name">Nume căutare</Label>
            <Input
              id="saved-search-name"
              value={saveSearchName}
              onChange={(event) => setSaveSearchName(event.target.value)}
              placeholder="Ex: Știri ZF + BVB"
            />
            {saveError && (
              <p className="text-sm text-destructive">{saveError}</p>
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setIsSaveDialogOpen(false)}>
              Renunță
            </Button>
            <Button onClick={handleSaveSearch}>Salvează</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <EditSavedSearchDialog
        open={isEditDialogOpen}
        onOpenChange={setIsEditDialogOpen}
        activeSavedSearch={activeSavedSearch}
        symbolValues={symbolValues}
      />
    </div>
  );
}
